const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ANIMATION_REVEALED_CLASSNAME = 'scroll-trigger--revealed';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic.
// An element is hidden (--offscreen) only while it waits below the screen. It is revealed
// when it scrolls in, when it has already been scrolled past (a fast fling or a jump can skip
// it without it ever intersecting), and before printing. So a missed observer callback can
// never leave content invisible.
function revealScrollTrigger(element, order) {
  if (!element.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) return;
  element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
  if (order === undefined) return;
  if (element.hasAttribute('data-cascade')) element.style.setProperty('--animation-order', order);
  element.classList.add(SCROLL_ANIMATION_REVEALED_CLASSNAME);
}

function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    const elementTarget = element.target;
    if (element.isIntersecting) {
      revealScrollTrigger(elementTarget, index);
      observer.unobserve(elementTarget);
    } else if (element.boundingClientRect.top < 0) {
      revealScrollTrigger(elementTarget);
      observer.unobserve(elementTarget);
    } else {
      elementTarget.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      elementTarget.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  // No animation means nothing to hide: with reduced motion the content just stays visible.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));

  // The observer only reports changes in intersection. A jump or fast fling from below an
  // element to above it is "not intersecting" both times, so no callback comes and the element
  // would stay hidden. After each scroll, anything still hidden that is no longer below the
  // screen is revealed (animated if it is on screen, instantly if it was skipped).
  let scrollCheckQueued = false;
  const revealPassed = () => {
    scrollCheckQueued = false;
    const viewportBottom = window.innerHeight;
    animationTriggerElements.forEach((element) => {
      if (!element.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) return;
      const rect = element.getBoundingClientRect();
      if (rect.top >= viewportBottom) return;
      revealScrollTrigger(element, rect.bottom > 0 ? 0 : undefined);
      observer.unobserve(element);
    });
  };
  window.addEventListener(
    'scroll',
    () => {
      if (scrollCheckQueued) return;
      scrollCheckQueued = true;
      setTimeout(() => requestAnimationFrame(revealPassed), 150);
    },
    { passive: true }
  );

  window.addEventListener('beforeprint', () => {
    animationTriggerElements.forEach((element) => revealScrollTrigger(element));
  });
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
